import { create } from 'zustand';
import { UserData } from '../types/userData';
import { getJwt, refreshToken } from '../helpers/api/callAuthentification';
import { persist, devtools, createJSONStorage } from 'zustand/middleware';
import { isTokenExpired } from '../helpers/functions/tokenUtils';
import Cookies from 'universal-cookie';

type UserStoreType = {
    user: UserData;
    setUser: (user: UserData) => void;
    login(email: string, password: string): Promise<boolean>;
    isAccessTokenExpired: () => boolean;
    isRefreshTokenExpired: () => boolean;
    refreshAccessToken: () => Promise<boolean>;
    logout: () => void;
    isAuthenticated: boolean;
};

const initialUser: UserData = {
    id: '',
    civility: '',
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    profile: '',
    trainingCenter: '',
    societyId: '',
    nullSocietyId: '',
    access_token: '',
    refresh_token: '',
    expires_in: 0,
    refresh_expires_in: 0
};

const cookies = new Cookies();

export const useUserStore = create<UserStoreType>()(
    devtools(
        persist(
            (set, get) => ({
                user: initialUser,
                isAuthenticated: false,
                setUser: (user: UserData) => {
                    const updatedUser = { ...get().user, ...user };
                    set({
                        user: updatedUser,
                        isAuthenticated: updatedUser.access_token !== '' && updatedUser.id !== ''
                    });
                },
                login: async (email: string, password: string) => {
                    try {
                        const jwt = await getJwt(email, password);
                        if (!jwt) throw new Error('Login failed');

                        set({ user: { ...initialUser, ...jwt }, isAuthenticated: true });

                        const updatedUser = get().user;

                        const refreshTokenExpiration = updatedUser.refresh_expires_in ? updatedUser.refresh_expires_in * 1000 : 0;
                        const expirationDate = new Date(new Date().getTime() + refreshTokenExpiration);

                        cookies.set('SE_RefreshToken', updatedUser.refresh_token, {
                            path: '/',
                            secure: true,
                            expires: expirationDate
                        });

                        return true;
                    } catch (err) {
                        console.error('Login error:', err);
                        set({ user: initialUser, isAuthenticated: false });
                        cookies.remove('SE_RefreshToken');
                        return false;
                    }
                },
                isAccessTokenExpired: () => {
                    const accessToken = get().user.access_token;
                    return !accessToken || isTokenExpired(accessToken);
                },
                isRefreshTokenExpired: () => {
                    const refreshToken = get().user.refresh_token;
                    return !refreshToken || isTokenExpired(refreshToken);
                },
                refreshAccessToken: async () => {
                    const refresh_token = cookies.get('SE_RefreshToken');
                    if (!refresh_token) {
                        return false;
                    }

                    const newJwt = await refreshToken(refresh_token);
                    if (newJwt) {
                        set({ user: { ...get().user, ...newJwt } });

                        const refreshTokenExpiration = newJwt.refresh_expires_in * 1000;
                        const expirationDate = new Date(new Date().getTime() + refreshTokenExpiration);

                        cookies.set('SE_RefreshToken', newJwt.refresh_token, {
                            path: '/',
                            secure: true,
                            expires: expirationDate
                        });

                        return true;
                    }
                    return false;
                },
                logout: () => {
                    set({ user: initialUser, isAuthenticated: false });
                    cookies.remove('SE_RefreshToken');
                }
            }),
            {
                name: 'SE_User',
                storage: createJSONStorage(() => sessionStorage)
            }
        )
    )
);
